import { Box, Button } from '@mui/material';
import BScroll, { type Options } from 'better-scroll';
import { useEffect, useRef } from 'react';
import i18n from '../../i18n';
import './stacked-filter-tabs.component.scss';

/** 横向拖动滚动配置（对齐 POS tableScrollBar / ScrollTabs + better-scroll） */
const HORIZONTAL_BSCROLL_OPTIONS: Partial<Options> = {
    scrollX: true,
    scrollY: false,
    observeDOM: true,
    click: true,
    probeType: 1,
    mouseWheel: true,
    disableMouse: false,
    nestedScroll: true,
    bounce: false,
};

/** 单项：id + 展示用 i18n key，或接口下发的 `labelText`（优先） */
export type StackedFilterTabItem<T extends string = string> = {
    id: T;
    /** 静态 Tab：传 i18n 键 */
    labelI18nKey?: string;
    /** 动态 Tab（如员工等级名）：与 labelI18nKey 二选一即可 */
    labelText?: string;
};

export type StackedFilterTabsClassNames = {
    tablist: string;
    tabsContent: string;
    tabBox: string;
    tabBoxActive: string;
    tabButton: string;
};

const DEFAULT_CLASS_NAMES: StackedFilterTabsClassNames = {
    tablist: 'stacked-filter-tabs',
    tabsContent: 'stacked-filter-tabs__track',
    tabBox: 'stacked-filter-tabs__tab',
    tabBoxActive: 'stacked-filter-tabs__tab--active',
    tabButton: 'stacked-filter-tabs__button',
};

export type StackedFilterTabsProps<T extends string = string> = {
    tabs: StackedFilterTabItem<T>[];
    value: T;
    onChange: (id: T) => void;
    /** 覆盖默认 BEM（与组件自带 `stacked-filter-tabs.component.scss` 解耦） */
    classNames?: Partial<StackedFilterTabsClassNames>;
};

/**
 * 横向叠层筛选 Tab（与 scroll-tabs 一致：选中 z-index 999，否则左侧叠在右侧上）。
 * 横向滚动使用 better-scroll 拖动（对齐 POS 桌台区 tableScrollBar / ScrollTabs），非原生 overflow 中键平移。
 */
export default function StackedFilterTabs<T extends string = string>({
    tabs,
    value,
    onChange,
    classNames: classNamesProp,
}: StackedFilterTabsProps<T>) {
    const cn = { ...DEFAULT_CLASS_NAMES, ...classNamesProp };
    /** BScroll 挂载的外层容器（overflow hidden） */
    const scrollHostRef = useRef<HTMLDivElement | null>(null);
    /** better-scroll 实例，Tab 增删或文案变化后 refresh */
    const bScrollRef = useRef<BScroll | null>(null);

    useEffect(() => {
        const host = scrollHostRef.current;
        if (!host) {
            return undefined;
        }
        bScrollRef.current = new BScroll(host, HORIZONTAL_BSCROLL_OPTIONS as Options);
        return () => {
            bScrollRef.current?.destroy();
            bScrollRef.current = null;
        };
    }, []);

    useEffect(() => {
        const instance = bScrollRef.current;
        if (!instance) {
            return;
        }
        const frameId = requestAnimationFrame(() => {
            instance.refresh();
        });
        return () => cancelAnimationFrame(frameId);
    }, [tabs, value]);

    return (
        <div ref={scrollHostRef} className={cn.tablist}>
            <div className="stacked-filter-tabs__scroll-inner">
                <Box className={cn.tabsContent} display="flex" role="tablist">
                    {tabs.map((tab, index) => {
                        const selected = tab.id === value;
                        /** 与 scroll-tabs 一致：选中 999，否则左侧叠在右侧上 */
                        const stackZ = selected ? 999 : tabs.length - index;
                        return (
                            <Box
                                key={tab.id}
                                sx={{ zIndex: stackZ }}
                                className={selected ? `${cn.tabBox} ${cn.tabBoxActive}` : cn.tabBox}
                            >
                                <Button
                                    type="button"
                                    role="tab"
                                    aria-selected={selected}
                                    variant="text"
                                    disableRipple
                                    className={cn.tabButton}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onChange(tab.id);
                                    }}
                                >
                                    {tab.labelText != null && String(tab.labelText).length > 0
                                        ? tab.labelText
                                        : tab.labelI18nKey
                                          ? i18n.t(tab.labelI18nKey)
                                          : ''}
                                </Button>
                            </Box>
                        );
                    })}
                </Box>
            </div>
        </div>
    );
}
